import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { HTTPError } from 'ky';
import prettyBytes from 'pretty-bytes';

import { createPublicRocClient } from '@/adapters/publicROCApi';
import {
  getSignedUploadContentType,
  uploadFileToSignedUrl
} from '@/api/postS3Files';
import { getS3UploadErrorMessage } from '@/api/s3UploadFileTypes';
import { Dropzone } from '@/components/Dropzone';
import {
  CHAT_ATTACHMENT_MAX_FILE_SIZE_BYTES,
  CHAT_ATTACHMENT_MAX_FILES,
  getChatAttachmentFileKey
} from '@/components/roc/chatAttachmentUpload';
import { sanitizeRocFileName } from '@/components/roc/sanitizeRocFileName';
import { useSnackbar } from '@/hooks/useSnackbar';
import { SUPPORTED_CONTENT_TYPES } from '@/pages/CRM/Screener/models/LeadModel';
import type { DocumentUploadUrl } from '@/types/roc/models/DocumentUploadUrl';
import { ResponseError } from '@/types/roc/runtime';

interface PendingFile {
  id: string;
  file: File;
  error?: string;
}

interface PublicReferralAttachDialogProps {
  open: boolean;
  onClose: () => void;
  invitationToken: string;
  sessionToken: string;
  referralId: string;
  onUploaded: () => Promise<void> | void;
}

function getUploadErrorMessage(error: unknown, fileName?: string) {
  if (error instanceof HTTPError) {
    const status = error.response.status;
    if (status === 401 || status === 403) {
      return 'This referral link is invalid or no longer available.';
    }
    if (status === 409) {
      return fileName
        ? `${fileName} was already uploaded. Refresh and try again.`
        : 'This document was already uploaded. Refresh and try again.';
    }
    if (status === 413) {
      return fileName
        ? `${fileName} is too large to upload.`
        : 'A file is too large to upload.';
    }
    if (status >= 500) {
      return 'Server error while uploading. Try again later.';
    }
  }

  if (error instanceof ResponseError) {
    const status = error.response.status;
    if (status === 401 || status === 403) {
      return 'This referral link is invalid or no longer available.';
    }
    if (status === 409) {
      return fileName
        ? `${fileName} was already uploaded. Refresh and try again.`
        : 'This document was already uploaded. Refresh and try again.';
    }
    if (status === 413) {
      return fileName
        ? `${fileName} is too large to upload.`
        : 'A file is too large to upload.';
    }
    if (status >= 500) {
      return 'Server error while uploading. Try again later.';
    }
  }

  return getS3UploadErrorMessage(
    error,
    "Couldn't reach the server. Check your connection and try again."
  );
}

export function PublicReferralAttachDialog({
  open,
  onClose,
  invitationToken,
  sessionToken,
  referralId,
  onUploaded
}: PublicReferralAttachDialogProps) {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const reset = () => {
    setFiles([]);
    setIsUploading(false);
  };

  const handleClose = () => {
    if (isUploading) {
      return;
    }
    reset();
    onClose();
  };

  const handleDrop = (acceptedFiles: File[]) => {
    setFiles((currentFiles) => {
      const existingKeys = new Set(
        currentFiles.map((file) => getChatAttachmentFileKey(file.file))
      );
      const nextFiles = [...currentFiles];

      for (const file of acceptedFiles) {
        if (existingKeys.has(getChatAttachmentFileKey(file))) {
          continue;
        }

        if (nextFiles.length >= CHAT_ATTACHMENT_MAX_FILES) {
          showSnackbar({
            message: `Maximum ${CHAT_ATTACHMENT_MAX_FILES} files allowed.`,
            severity: 'warning'
          });
          break;
        }

        nextFiles.push({
          id: crypto.randomUUID(),
          file
        });
      }

      return nextFiles;
    });
  };

  const handleRemove = (id: string) => {
    setFiles((currentFiles) =>
      currentFiles.filter((pendingFile) => pendingFile.id !== id)
    );
  };

  const handleUpload = async () => {
    if (files.length === 0 || isUploading) {
      return;
    }

    setIsUploading(true);

    const rocClient = createPublicRocClient(sessionToken, invitationToken);
    const failedFiles: PendingFile[] = [];
    let succeededCount = 0;

    try {
      for (const pendingFile of files) {
        const normalizedFileName = sanitizeRocFileName(pendingFile.file.name);
        const contentType = getSignedUploadContentType(pendingFile.file);
        const documentId = crypto.randomUUID();

        try {
          const uploadUrlResponse: DocumentUploadUrl =
            await rocClient.createDocumentUploadUrl({
              referralId,
              createDocumentUploadUrlRequest: {
                document_id: documentId,
                file_name: normalizedFileName,
                content_type: contentType
              }
            });

          await uploadFileToSignedUrl(
            pendingFile.file,
            uploadUrlResponse.signed_url
          );

          await rocClient.createDocument({
            referralId,
            createDocumentRequest: {
              document_id: documentId,
              file_name: normalizedFileName,
              source_blob_s3_key: uploadUrlResponse.source_blob_s3_key,
              content_type: contentType,
              file_size_bytes: pendingFile.file.size,
              document_type: 'attachment'
            }
          });

          succeededCount += 1;
        } catch (error) {
          failedFiles.push({
            ...pendingFile,
            error: getUploadErrorMessage(error, pendingFile.file.name)
          });
        }
      }

      if (succeededCount > 0) {
        await onUploaded();
      }

      if (failedFiles.length === 0) {
        reset();
        onClose();
        return;
      }

      setFiles(failedFiles);

      if (succeededCount === 0) {
        showSnackbar({
          message: failedFiles[0]?.error ?? 'Failed to upload documents.',
          severity: 'error'
        });
      } else {
        showSnackbar({
          message: `${succeededCount} document(s) uploaded. ${failedFiles.length} need to be retried.`,
          severity: 'warning'
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attach Documents</DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <Dropzone
            accept={SUPPORTED_CONTENT_TYPES}
            maxSize={CHAT_ATTACHMENT_MAX_FILE_SIZE_BYTES}
            multiple={true}
            disabled={isUploading}
            onDrop={handleDrop}
          />

          {files.length > 0 && (
            <Stack gap={1}>
              {files.map((pendingFile) => (
                <Box
                  key={pendingFile.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    border: '1px solid',
                    borderColor: pendingFile.error ? 'error.main' : 'divider',
                    borderRadius: 1,
                    px: 1.5,
                    py: 1
                  }}
                >
                  <Stack minWidth={0}>
                    <Typography noWrap>{pendingFile.file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {prettyBytes(pendingFile.file.size)}
                    </Typography>
                    {pendingFile.error && (
                      <Stack direction="row" gap={0.5} alignItems="center">
                        <ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} />
                        <Typography variant="caption" color="error">
                          {pendingFile.error}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {isUploading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Tooltip title="Remove file">
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(pendingFile.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={files.length === 0 || isUploading}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
